/**
 * Orçamentos com itens do Relatório por Item.
 *
 * Período e dimensões entram na query antes da paginação (#50). O cache offline
 * passa pelos mesmos predicados positivos; `document_type` nulo continua sendo
 * orçamento legado (#82).
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllPages } from '@/lib/reportPaging';
import { toItemsDocument, type ItemsReportDocument } from '@/lib/itemsReport';
import { isSaleDocument } from '@/lib/quickSale';
import { todayIsoDate } from '@/lib/financeStatus';
import { getCachedQuotes } from '@/lib/offlineDataCache';
import {
  isReportQuote,
  quoteMatchesReportFilters,
  reportQuoteItems,
  type ReportFilterState,
} from '@/lib/reportFilters';

/** Exclusão nullable-safe do PDV. `.neq` puro apagaria o legado com NULL. */
export const EXCLUDE_PDV_OR_FILTER = 'document_type.is.null,document_type.neq.pedido';

export interface ReportItemsResult {
  documents: ItemsReportDocument[];
  truncated: boolean;
}

export interface ReportItemsOptions {
  range: { from: Date; to: Date };
  filters: ReportFilterState;
  enabled: boolean;
}

/** `timestamptz` → `YYYY-MM-DD` no calendário local. */
function localIsoDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : todayIsoDate(parsed);
}

function cachedDocuments(
  rows: readonly any[],
  companyId: string,
  options: Pick<ReportItemsOptions, 'range' | 'filters'>,
): ItemsReportDocument[] {
  return rows
    .filter((row) => !row?.company_id || row.company_id === companyId)
    .filter((row) => {
      const createdAt = new Date(row?.created_at);
      return !Number.isNaN(createdAt.getTime())
        && createdAt >= options.range.from
        && createdAt <= options.range.to;
    })
    .filter(isReportQuote)
    .filter((row) => quoteMatchesReportFilters(row, options.filters, 'items'))
    .map((row) => {
      const quoteItems = reportQuoteItems(row, options.filters.productId);
      return toItemsDocument(
        { ...row, quote_items: quoteItems },
        localIsoDate(row?.created_at),
      );
    });
}

/**
 * A assinatura por objeto é intencional (#71): filtro novo obriga o `tsc -b` a
 * apontar cada call-site em vez de deixar um chamador silenciosamente antigo.
 */
export function useReportItems(options: ReportItemsOptions) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const { range, filters, enabled } = options;

  const fromIso = range.from.toISOString();
  const toIso = range.to.toISOString();
  const clientId = filters.clientId;
  const productId = filters.productId;
  const status = filters.statusByTab.items;

  const query = useQuery<ReportItemsResult>({
    queryKey: ['report-items', companyId, fromIso, toIso, clientId, productId, status],
    enabled: Boolean(user?.id && companyId) && enabled,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => {
      if (!user?.id || !companyId) return undefined;
      return {
        documents: cachedDocuments(getCachedQuotes(user.id) ?? [], companyId, {
          range,
          filters,
        }),
        truncated: false,
      };
    },
    queryFn: async () => {
      if (!companyId) return { documents: [], truncated: false };

      const itemRelation = productId ? 'quote_items!inner' : 'quote_items';
      const result = await fetchAllPages<any>((start, end) => {
        let query: any = supabase
          .from('quotes')
          .select(
            'id, status, created_at, client_id, client_name, document_type,'
            + ` ${itemRelation}(product_id, name, item_type, unit, quantity, unit_price,`
            + ' unit_cost, discount, discount_type, finance_entry_id)',
          )
          .eq('company_id', companyId)
          .or(EXCLUDE_PDV_OR_FILTER)
          .gte('created_at', fromIso)
          .lte('created_at', toIso);

        // BLOCO DE FILTROS DO SERVIDOR: deve permanecer dentro de fetchAllPages.
        if (clientId) query = query.eq('client_id', clientId);
        if (status) query = query.eq('status', status);
        if (productId) query = query.eq('quote_items.product_id', productId);

        return query
          .order('created_at', { ascending: false })
          .order('id', { ascending: true })
          .range(start, end);
      });

      return {
        documents: result.rows
          .filter((row) => !isSaleDocument(row))
          .filter((row) => quoteMatchesReportFilters(row, filters, 'items'))
          .map((row) => toItemsDocument(row, localIsoDate(row?.created_at))),
        truncated: result.truncated,
      };
    },
  });

  return {
    documents: query.data?.documents ?? [],
    truncated: query.data?.truncated ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
